import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import * as interviewActions from "../../../actions/interviewActions";
import { Table } from "@coverme/table";
import { AddAdditional } from "@coverme/forms";
import * as stringService from "../../../services/stringService";
import { Link } from "react-router-dom";
import { ConfirmationModal } from "../../../components/ConfirmationModal/ConfirmationModal";
import { WizardButtons } from "../Components/WizardButtons";
import { getAge } from "../../../services/stringService";

function createColumns(patient, responsibleParty, baseUrl, deletePerson) {
    const columns = [
        {
            Header: "Full Name",
            accessor: "fullName",
            Cell: function FullName({ original }) {
                return stringService.getFormattedNameFromObject(original);
            },
        },
        {
            Header: "Age",
            headerClassName: "align-center",
            className: "align-center",
            accessor: "age",
            width: 75,
        },
        {
            Header: `Relationship To ${stringService.getCustomFormattedNameFromObject(patient)}`,
            accessor: "relationshipToPatient",
            Cell: function RelationshipToPatient({ original, value, ...props }) {
                if (original.personId === patient.personId) {
                    return "Self";
                } else {
                    return value;
                }
            },
        },
        {
            Header: <span>Actions</span>,
            headerClassName: "table__links-header",
            className: "table__links-row",
            accessor: "personId", // String-based value accessors!
            minWidth: 150,
            Cell: function TableActions(props) {
                if (props.value === patient.personId || props.value === responsibleParty?.personId) {
                    return <span />;
                }
                return (
                    <span className="table__links">
                        <Link className="table__link" to={`${baseUrl}/${props.value}`}>
                            edit
                        </Link>
                        <span className="table__link-separator"> | </span>
                        <a
                            className="table__link"
                            href="javascript:void(0)"
                            onClick={() => {
                                deletePerson(props.value);
                            }}
                        >
                            delete
                        </a>
                    </span>
                );
            },
        },
    ];

    return columns;
}

function getHousehold(patient, responsibleParty, householdMembers, includeResponsibleParty) {
    let fullHousehold = [patient];
    if (includeResponsibleParty) {
        fullHousehold = [...fullHousehold, responsibleParty];
    }

    fullHousehold = [...fullHousehold, ...householdMembers];
    return fullHousehold;
}

const InterviewHouseholdBuilder = ({
    patient,
    householdMembers,
    responsibleParty,
    includeResponsibleParty,
    onDeleteHouseholdMember,
    navigateToNextPage,
    navigateToPreviousPage,
    history,
    match,
}) => {
    const [patientToDelete, setPatientToDelete] = useState(null);
    const household = useMemo(() => {
        return getHousehold(patient, responsibleParty, householdMembers, includeResponsibleParty);
    }, [patient, responsibleParty, householdMembers, includeResponsibleParty]);

    const columns = createColumns(patient, responsibleParty, match.url, setPatientToDelete);

    return (
        <div className="wizard-page">
            <h2 className="title">Household Builder</h2>
            <ConfirmationModal
                isShown={patientToDelete != null}
                onClose={() => {
                    setPatientToDelete(null);
                }}
                onConfirm={() => {
                    onDeleteHouseholdMember(patientToDelete);
                    setPatientToDelete(null);
                }}
                body={"Are you sure you wish to delete this household member?"}
            />
            <div className="interview-table-container">
                <div className="table-title" id="household-bullet-list">
                    {getAge(patient.dob) >= 21 && (
                        <p>
                            <p>
                                {
                                    "Include these people even if they aren't applying for health coverage for themselves:"
                                }
                            </p>
                            <ul>
                                <li>{"Any spouse"}</li>
                                <li>{"Any child under age 21 they live with, including stepchildren"}</li>
                                <li>
                                    {"Any other person on the same federal income tax return (including any children over " +
                                        "age 21 who are claimed on a parent's tax return)"}
                                </li>
                                <li>{"You don't need to file taxes to get health coverage"}</li>
                            </ul>
                        </p>
                    )}

                    {getAge(patient.dob) < 21 && (
                        <p>
                            <p>
                                {
                                    "Include these people even if they aren't applying for health coverage for themselves:"
                                }
                            </p>
                            <ul>
                                <li>{"Any parent (or step parent) they live with"}</li>
                                <li>{"Any sibling they live with"}</li>
                                <li>{"Any child they live with, including stepchildren"}</li>
                                <li>{"Any spouse they live with"}</li>
                                <li>{"Any other person on the same federal income tax return"}</li>
                                <li>{"You don't need to file taxes to get health coverage"}</li>
                            </ul>
                        </p>
                    )}

                    <AddAdditional
                        labelText={"Add Household Member"}
                        clickEvent={() => {
                            history.push(`${match.url}/add`);
                        }}
                    />
                </div>
                <Table
                    data={household}
                    columns={columns}
                    sortable={false}
                    resizable={true}
                    showPagination={false}
                    defaultPageSize={100}
                />
            </div>
            <WizardButtons
                formik={{}}
                navigateToNextPage={navigateToNextPage}
                navigateToPreviousPage={navigateToPreviousPage}
                isFirstPage={false}
            />
        </div>
    );
};

export const InterviewHouseholdBuilderContainer = connect(
    state => ({
        patient: state.interview.entry.patient,
        householdMembers: state.interview.householdMembers,
        responsibleParty: state.interview.entry.responsibleParty,
        includeResponsibleParty: state.interview.entry.isResponsiblePartyDifferentFromPatient,
    }),
    dispatch => ({
        onSubmit: data => dispatch(interviewActions.setHouseholdMembers(data.householdMembers)),
        onDeleteHouseholdMember: memberId => dispatch(interviewActions.deleteHouseholdMember(memberId)),
    })
)(InterviewHouseholdBuilder);
